import { SeededRandom } from "../core/random";
import type { GameHud, GameInstance, GameServices } from "../core/types";
import { clamp, drawPanel, drawScanlines, setText } from "./drawing";

const ROUND_SECONDS = 60;
const MAX_BREACHES = 6;
const BOSS_APPEARS_AT = 30;
const BOSS_HP = 20;
const BOSS_TIMER = 20;

interface Target {
  originX: number;
  originY: number;
  age: number;
  lifetime: number;
}

export class Sector403Game implements GameInstance {
  private readonly random = new SeededRandom(Date.now() ^ 0x0403);
  private readonly targets: Target[] = [];
  private score = 0;
  private time = ROUND_SECONDS;
  private elapsed = 0;
  private spawnTimer = 0.5;
  private fireCooldown = 0;
  private muzzleFlash = 0;
  private crosshairX = 640;
  private crosshairY = 360;
  private breaches = 0;
  private bossActive = false;
  private bossSpawned = false;
  private bossHp = BOSS_HP;
  private bossTimer = BOSS_TIMER;
  private bossHitFlash = 0;
  private message = "Clear blocked requests before they reach the gateway";
  private messageTimer = 0;
  private ended = false;

  constructor(private readonly services: GameServices) {}

  advanceTime(deltaSeconds: number): void {
    if (this.ended) return;
    this.elapsed += deltaSeconds;
    this.time = Math.max(0, ROUND_SECONDS - this.elapsed);
  }

  update(deltaSeconds: number): void {
    if (this.ended) return;

    this.fireCooldown = Math.max(0, this.fireCooldown - deltaSeconds);
    this.muzzleFlash = Math.max(0, this.muzzleFlash - deltaSeconds);
    this.bossHitFlash = Math.max(0, this.bossHitFlash - deltaSeconds);
    this.messageTimer = Math.max(0, this.messageTimer - deltaSeconds);
    if (this.messageTimer === 0 && !this.bossActive) {
      this.message = "Clear blocked requests before they reach the gateway";
    }

    if (this.time === 0) {
      this.finish("Sector cleared");
      return;
    }

    if (!this.bossSpawned && this.elapsed >= BOSS_APPEARS_AT) {
      this.bossSpawned = true;
      this.bossActive = true;
      this.services.audio.play("finish");
      this.setMessage("Rogue policy core online - break it!", 2.4);
    }

    this.updateAim(deltaSeconds);
    this.updateTargets(deltaSeconds);
    if (this.ended) return;

    if (this.bossActive) {
      this.bossTimer -= deltaSeconds;
      if (this.bossTimer <= 0) {
        this.finish("Rogue core breached the gateway");
        return;
      }
    }

    if (this.services.input.consumePressed("primary") && this.fireCooldown === 0) {
      this.fire();
    }
  }

  render(context: CanvasRenderingContext2D): void {
    this.drawSector(context);
    if (this.bossActive) this.drawBoss(context);
    const ordered = [...this.targets].sort((left, right) => left.age - right.age);
    for (const target of ordered) this.drawTarget(context, target);
    this.drawHeader(context);
    this.drawCrosshair(context);
    this.drawFooter(context);
    drawScanlines(context, "rgba(255, 255, 255, 0.018)");
  }

  getHud(): GameHud {
    return { score: this.score, time: this.time, message: this.message, ended: this.ended };
  }

  destroy(): void {}

  private updateAim(deltaSeconds: number): void {
    const input = this.services.input;
    const horizontal = Number(input.isDown("right")) - Number(input.isDown("left"));
    const vertical = Number(input.isDown("down")) - Number(input.isDown("up"));
    const usingKeyboard = horizontal !== 0 || vertical !== 0;
    if (input.pointer.active && !usingKeyboard) {
      this.crosshairX = input.pointer.x;
      this.crosshairY = input.pointer.y;
    } else {
      this.crosshairX = clamp(this.crosshairX + horizontal * 470 * deltaSeconds, 45, 1235);
      this.crosshairY = clamp(this.crosshairY + vertical * 400 * deltaSeconds, 95, 650);
    }
  }

  private updateTargets(deltaSeconds: number): void {
    const cap = this.bossActive ? 3 : 5;
    this.spawnTimer -= deltaSeconds;
    if (this.spawnTimer <= 0 && this.targets.length < cap) {
      this.targets.push({
        originX: 640 + (this.random.integer(7) - 3) * 104 + this.random.between(-24, 24),
        originY: 350 + (this.random.integer(3) - 1) * 104 + this.random.between(-22, 22),
        age: 0,
        lifetime: this.random.between(2.3, 3.2),
      });
      const base = this.bossActive ? 1.1 : 0.86;
      this.spawnTimer = Math.max(0.44, base - this.elapsed * 0.004) + this.random.between(0, 0.26);
    }

    for (let index = this.targets.length - 1; index >= 0; index -= 1) {
      const target = this.targets[index];
      if (!target) continue;
      target.age += deltaSeconds;
      if (target.age < target.lifetime) continue;
      this.targets.splice(index, 1);
      this.breaches += 1;
      this.services.audio.play("miss");
      this.setMessage(`Blocked request slipped through - breach ${this.breaches}/${MAX_BREACHES}`, 1.8);
      if (this.breaches >= MAX_BREACHES) {
        this.finish("Sector integrity lost");
        return;
      }
    }
  }

  private targetPosition(target: Target): { x: number; y: number; radius: number } {
    const progress = clamp(target.age / target.lifetime, 0, 1);
    const expansion = 0.42 + progress * 0.8;
    return {
      x: 640 + (target.originX - 640) * expansion,
      y: 360 + (target.originY - 360) * expansion,
      radius: 20 + progress * 46,
    };
  }

  private fire(): void {
    this.fireCooldown = 0.13;
    this.muzzleFlash = 0.09;

    let hitIndex = -1;
    let closest = Number.POSITIVE_INFINITY;
    for (let index = 0; index < this.targets.length; index += 1) {
      const target = this.targets[index];
      if (!target) continue;
      const position = this.targetPosition(target);
      const distance = Math.hypot(position.x - this.crosshairX, position.y - this.crosshairY);
      if (distance <= position.radius * 1.12 && distance < closest) {
        closest = distance;
        hitIndex = index;
      }
    }

    if (hitIndex >= 0) {
      const target = this.targets[hitIndex];
      if (!target) return;
      const speedBonus = Math.max(0, Math.round((1 - target.age / target.lifetime) * 70));
      this.score += 130 + speedBonus;
      this.targets.splice(hitIndex, 1);
      this.services.audio.play("hit");
      this.setMessage("Blocked request cleared", 1.1);
      return;
    }

    if (this.bossActive) {
      const distance = Math.hypot(640 - this.crosshairX, 300 - this.crosshairY);
      if (distance <= 96) {
        this.bossHp -= 1;
        this.bossHitFlash = 0.12;
        this.score += 45;
        this.services.audio.play("hit");
        if (this.bossHp <= 0) {
          this.bossActive = false;
          this.score += 900;
          this.setMessage("Rogue core broken - sector stabilized", 2.6);
          this.services.audio.play("finish");
        } else {
          this.setMessage(`Core integrity ${this.bossHp}/${BOSS_HP}`, 1);
        }
        return;
      }
    }

    this.score = Math.max(0, this.score - 10);
    this.services.audio.play("miss");
    this.setMessage("Shot missed - reacquire", 1);
  }

  private setMessage(message: string, duration: number): void {
    this.message = message;
    this.messageTimer = duration;
    this.services.announce(message);
  }

  private finish(message: string): void {
    this.ended = true;
    this.message = message;
    this.services.audio.play("finish");
    this.services.announce(`${message}. Final score ${this.score}.`);
  }

  private drawSector(context: CanvasRenderingContext2D): void {
    const background = context.createLinearGradient(0, 0, 0, 720);
    background.addColorStop(0, "#0e0d18");
    background.addColorStop(0.55, "#131c2b");
    background.addColorStop(1, "#060b13");
    context.fillStyle = background;
    context.fillRect(0, 0, 1280, 720);

    context.strokeStyle = "rgba(159, 178, 194, 0.16)";
    context.lineWidth = 2;
    for (let depth = 1; depth <= 6; depth += 1) {
      const scale = depth / 6;
      const width = 1180 * scale;
      const height = 560 * scale;
      context.strokeRect(640 - width / 2, 330 - height / 2, width, height);
    }
    for (let index = 0; index < 8; index += 1) {
      const angle = index * (Math.PI / 4);
      context.beginPath();
      context.moveTo(640, 330);
      context.lineTo(640 + Math.cos(angle) * 900, 330 + Math.sin(angle) * 620);
      context.stroke();
    }
  }

  private drawBoss(context: CanvasRenderingContext2D): void {
    context.save();
    context.translate(640, 300);
    const pulse = this.services.reducedMotion ? 1 : 1 + Math.sin(this.elapsed * 6) * 0.04;
    context.scale(pulse, pulse);
    context.shadowColor = "#ff5d5d";
    context.shadowBlur = this.bossHitFlash > 0 ? 40 : 24;
    context.fillStyle = this.bossHitFlash > 0 ? "#5a1620" : "#2a0d16";
    context.strokeStyle = "#ff5d5d";
    context.lineWidth = 5;
    context.beginPath();
    for (let side = 0; side < 6; side += 1) {
      const angle = Math.PI / 6 + side * (Math.PI / 3);
      const px = Math.cos(angle) * 92;
      const py = Math.sin(angle) * 92;
      if (side === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    setText(context, 15, 900, "#ff9c9c", "center");
    context.fillText("ROGUE CORE", 0, 0);
    context.restore();

    context.fillStyle = "rgba(255, 255, 255, 0.12)";
    context.fillRect(490, 410, 300, 12);
    context.fillStyle = "#ff5d5d";
    context.fillRect(490, 410, Math.max(3, 300 * (this.bossHp / BOSS_HP)), 12);
    setText(context, 12, 800, "#ff9c9c", "center");
    context.fillText(`CORE ${Math.max(0, Math.ceil(this.bossTimer))}s`, 640, 400);
  }

  private drawTarget(context: CanvasRenderingContext2D, target: Target): void {
    const { x, y, radius } = this.targetPosition(target);
    context.save();
    context.translate(x, y);
    context.shadowColor = "#ff5d5d";
    context.shadowBlur = 18;
    context.fillStyle = "rgba(7, 13, 22, 0.92)";
    context.strokeStyle = "#ff5d5d";
    context.lineWidth = Math.max(3, radius * 0.08);
    context.beginPath();
    for (let side = 0; side < 6; side += 1) {
      const angle = Math.PI / 6 + side * (Math.PI / 3);
      const px = Math.cos(angle) * radius;
      const py = Math.sin(angle) * radius;
      if (side === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
    context.closePath();
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    setText(context, Math.max(9, radius * 0.3), 900, "#ff9c9c", "center");
    context.fillText("403", 0, 0);
    context.restore();
  }

  private drawHeader(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 42, 320, 92, "#ff7a1a");
    setText(context, 12, 850, "#ffca8a");
    context.fillText("SECURE WEB GATEWAY", 72, 72);
    setText(context, 20, 900, "#f7fbff");
    context.fillText(this.bossActive ? "CORE BREACH RISK" : "FILTERING SECTOR", 72, 106);

    setText(context, 11, 850, "#839aa5", "right");
    context.fillText("SECTOR INTEGRITY", 1232, 62);
    for (let index = 0; index < MAX_BREACHES; index += 1) {
      const x = 1006 + index * 38;
      context.fillStyle = index < MAX_BREACHES - this.breaches ? "#42e2c3" : "#3c4651";
      context.beginPath();
      context.arc(x, 94, 10, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawCrosshair(context: CanvasRenderingContext2D): void {
    const radius = 26 + (this.services.reducedMotion ? 0 : Math.sin(this.elapsed * 5) * 2);
    context.save();
    context.translate(this.crosshairX, this.crosshairY);
    context.strokeStyle = this.muzzleFlash > 0 ? "#ff7a1a" : "#f7fbff";
    context.lineWidth = 3;
    context.beginPath();
    context.arc(0, 0, radius, 0, Math.PI * 2);
    context.stroke();
    context.beginPath();
    context.moveTo(-radius - 14, 0);
    context.lineTo(-8, 0);
    context.moveTo(radius + 14, 0);
    context.lineTo(8, 0);
    context.moveTo(0, -radius - 14);
    context.lineTo(0, -8);
    context.moveTo(0, radius + 14);
    context.lineTo(0, 8);
    context.stroke();
    context.restore();
  }

  private drawFooter(context: CanvasRenderingContext2D): void {
    drawPanel(context, 42, 620, 1196, 70, "#ff7a1a");
    setText(context, 22, 850, "#f7fbff");
    context.fillText(this.message, 72, 662);
    setText(context, 12, 750, "#9cb2bd", "right");
    context.fillText("AIM  POINTER / ARROWS     FIRE  CLICK / SPACE", 1204, 662);
  }
}
